import React from 'react';
import { IconHeart, IconZap, IconMinus, IconPlus, IconSparkles } from './icons';

/**
 * VitalAdjuster - Interactive component for adjusting vitals with meal rating budget
 * Shows current vitals, allows +/- adjustment, and displays projected state
 *
 * @param {string} label - Display label (e.g., "Heal HP")
 * @param {string} vitalType - Type of vital: 'hp', 'stress', or 'hope'
 * @param {number} currentValue - Current character vital value
 * @param {number} maxValue - Maximum vital value
 * @param {number} adjustmentValue - Amount to adjust (healing/clearing/gaining)
 * @param {function} onAdjustmentChange - Callback(delta) when +/- buttons clicked
 * @param {number} remainingBudget - Meal rating points remaining to spend
 */
const VitalAdjuster = ({
  label,
  vitalType,
  currentValue = 0,
  maxValue = 0,
  adjustmentValue = 0,
  onAdjustmentChange,
  remainingBudget = 0
}) => {
  // Get icon component based on vital type
  const getIcon = () => {
    switch (vitalType) {
      case 'hp':
        return <IconHeart className="mr-2 text-red-500" />;
      case 'stress':
        return <IconZap className="mr-2 text-yellow-400" />;
      case 'hope':
        return <IconSparkles className="mr-2 text-blue-400" />;
      default:
        return null;
    }
  };

  // Get color classes based on vital type
  const getColors = () => {
    switch (vitalType) {
      case 'hp':
        return {
          text: 'text-red-300',
          border: 'border-red-600',
          filled: 'bg-red-600 shadow-red-600/50',
          empty: 'bg-gray-800 border-red-800',
          hover: 'hover:bg-red-900'
        };
      case 'stress':
        return {
          text: 'text-yellow-400',
          border: 'border-yellow-600',
          filled: 'bg-yellow-600 shadow-yellow-600/50',
          empty: 'bg-gray-800 border-yellow-800',
          hover: 'hover:bg-yellow-900'
        };
      case 'hope':
        return {
          text: 'text-blue-400',
          border: 'border-blue-600',
          filled: 'bg-blue-600 shadow-blue-600/50',
          empty: 'bg-gray-800 border-blue-800',
          hover: 'hover:bg-blue-900'
        };
      default:
        return {
          text: 'text-gray-300',
          border: 'border-gray-600',
          filled: 'bg-gray-600',
          empty: 'bg-gray-800',
          hover: 'hover:bg-gray-900'
        };
    }
  };

  // Get verb for projected state
  const getVerb = () => {
    switch (vitalType) {
      case 'hp':
        return 'healing';
      case 'stress':
        return 'clearing';
      case 'hope':
        return 'gaining';
      default:
        return 'adjusting';
    }
  };

  const colors = getColors();
  const projectedValue = vitalType === 'stress'
    ? Math.max(0, currentValue - adjustmentValue) // Stress is cleared (decreases)
    : Math.min(maxValue, currentValue + adjustmentValue); // HP/Hope heal/gain (increase)

  // Calculate max adjustment allowed
  const maxAdjustmentAllowed = vitalType === 'stress'
    ? Math.min(remainingBudget, currentValue) // Can't clear more stress than you have
    : Math.min(remainingBudget, maxValue - currentValue); // Can't heal/gain beyond max

  // Disable buttons based on constraints
  const canDecrease = adjustmentValue > 0;
  const canIncrease = adjustmentValue < maxAdjustmentAllowed && remainingBudget > 0;

  return (
    <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
      {/* Header with icon and label */}
      <div className="flex items-center mb-2">
        {getIcon()}
        <span className={`font-semibold ${colors.text}`}>{label}</span>
      </div>

      {/* Current state */}
      <div className="text-sm text-gray-400 mb-3">
        Current: <span className="font-medium text-gray-200">{currentValue}/{maxValue}</span>
      </div>

      {/* Adjustment controls */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => onAdjustmentChange(-1)}
          disabled={!canDecrease}
          className={`p-1 rounded-full bg-gray-800 text-gray-300 ${colors.hover} hover:text-white border border-gray-600 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-800`}
        >
          <IconMinus />
        </button>

        {/* Visual boxes showing adjustment amount */}
        <div className="flex space-x-1 px-3">
          {Array.from({ length: Math.min(12, maxAdjustmentAllowed) }, (_, i) => (
            <div
              key={i}
              className={`w-4 h-4 border-2 ${colors.border} rounded-sm shadow-sm transition-colors ${
                i < adjustmentValue ? colors.filled : colors.empty
              }`}
              title={`${vitalType.toUpperCase()} adjustment ${i + 1}/${maxAdjustmentAllowed}`}
            />
          ))}
          {maxAdjustmentAllowed > 12 && (
            <span className="text-xs text-gray-500 ml-1">+{maxAdjustmentAllowed - 12}</span>
          )}
        </div>

        <button
          onClick={() => onAdjustmentChange(1)}
          disabled={!canIncrease}
          className={`p-1 rounded-full bg-gray-800 text-gray-300 ${colors.hover} hover:text-white border border-gray-600 transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-800`}
        >
          <IconPlus />
        </button>
      </div>

      {/* Adjustment amount */}
      <div className="text-center mb-2">
        <span className="text-2xl font-bold text-gray-100">{adjustmentValue}</span>
        <span className="text-sm text-gray-400 ml-2">points</span>
      </div>

      {/* Projected state */}
      {adjustmentValue > 0 && (
        <div className="text-sm text-gray-400 text-center">
          → <span className={`font-medium ${colors.text}`}>{projectedValue}/{maxValue}</span> after {getVerb()}
        </div>
      )}
    </div>
  );
};

export default VitalAdjuster;
